/**
 * Axis bounds. A collapsible block of six numeric inputs -
 * X/Y/Y-right min & max - where a blank field means "auto". Mirrors the blob:
 * the section is hidden for histograms, the X bounds are disabled when the X
 * axis is time, and the Y-right bounds are disabled with no right series. The
 * header shows a `(N set)` count of active bounds.
 *
 * Editing a field that affects the data window calls `doPlot()` (§10.8 seam);
 * the values themselves live in `plotState.axis_bounds`.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  plotState,
  setAxisBound,
  axisBoundsActiveCount,
  isTimeAxis,
  type PlotState,
  type AxisBoundKey,
} from '../../state/plot-state.ts';
import { doPlot } from './plot-actions.ts';

interface BoundField {
  key: AxisBoundKey;
  label: string;
}

const FIELDS: BoundField[] = [
  { key: 'x_min', label: 'X min' },
  { key: 'x_max', label: 'X max' },
  { key: 'y_min', label: 'Y min' },
  { key: 'y_max', label: 'Y max' },
  { key: 'yr_min', label: 'Y (R) min' },
  { key: 'yr_max', label: 'Y (R) max' },
];

@customElement('axis-bounds')
export class AxisBoundsControl extends LightElement {
  /**
   * Rendered inside another disclosure (the Plot card's Advanced section), so
   * it drops its own header and collapse rather than nesting one inside the
   * other.
   */
  @property({ type: Boolean }) embedded = false;

  @state() private collapsed = true;
  private store = new StoreController(this, [plotState]);

  private commit(key: AxisBoundKey, raw: string): void {
    const trimmed = raw.trim();
    let value: number | null = null;
    if (trimmed !== '') {
      const n = Number(trimmed);
      value = Number.isFinite(n) ? n : null;
    }
    setAxisBound(key, value);
    if (plotState.value.y_left.length || plotState.value.y_right.length) doPlot();
  }

  private isDisabled(key: AxisBoundKey, s = plotState.value): boolean {
    if ((key === 'x_min' || key === 'x_max') && s.plot_type === 'line' && isTimeAxis(s)) {
      return true;
    }
    if ((key === 'yr_min' || key === 'yr_max') && s.y_right.length === 0) return true;
    return false;
  }

  private renderGrid(s: PlotState): TemplateResult {
    return html`
      <div class="axis-bounds-grid">
        ${FIELDS.map(
          (f) => html`
            <label>${f.label}</label>
            <input
              type="text"
              class="axis-bound-input"
              placeholder="auto"
              ?disabled=${this.isDisabled(f.key, s)}
              .value=${s.axis_bounds[f.key] == null ? '' : String(s.axis_bounds[f.key])}
              @change=${(e: Event) => this.commit(f.key, (e.target as HTMLInputElement).value)}
            />
          `,
        )}
      </div>
    `;
  }

  override render(): TemplateResult {
    const s = plotState.value;
    if (s.plot_type === 'histogram') return html`${nothing}`;
    if (this.embedded) {
      return html`<div class="axis-block">
        <label>Axis bounds</label>
        ${this.renderGrid(s)}
      </div>`;
    }
    const n = axisBoundsActiveCount(s);
    return html`
      <div class="axis-bounds-wrap">
        <div class="section-header" @click=${() => (this.collapsed = !this.collapsed)}>
          <span class="arrow ${this.collapsed ? '' : 'open'}">▶</span>
          <span>Axis bounds</span>
          <span class="count">${n > 0 ? `(${n} set)` : ''}</span>
        </div>
        <div class="section-body ${this.collapsed ? 'collapsed' : ''}">
          ${this.renderGrid(s)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'axis-bounds': AxisBoundsControl;
  }
}
